import React from "react";
import { Link } from "react-router-dom";

function RefundPolicyHero() {
  const scrollToSection = (event, sectionId) => {
    event.preventDefault();

    const element = document.getElementById(sectionId);
    const offset = 80;

    if (element) {
      const offsetPosition = element.offsetTop - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      id="top"
      className="relative z-10 overflow-hidden py-16 md:py-[80px] xl:py-[100px]"
    >
      <div className="container">
        <h1 className="my-4 md:my-8 py-[40px] md:py-[120px] xl:py-[150px] text-3xl sm:text-4xl md:text-5xl font-bold leading-tight text-center text-black bg-slate-100/90 rounded">
          Refund Policy (TOS)
        </h1>
        <div className="-mx-4 pt-2 md:pt-4 flex flex-wrap">
          {/* Sidebar for Table of Contents */}
          <div className="hidden md:block md:w-1/4 lg:w-1/5 xl:w-1/6 pl-8 pr-4">
            <div className="sticky top-20">
              <h2 className="text-lg font-bold mb-4">Table of Contents</h2>
              <ul className="space-y-2 text-neutral-500">
                <li>
                  <a
                    href="#1"
                    className="hover:text-black"
                    onClick={(e) => scrollToSection(e, "1")}
                  >
                    Overview of the Refund Policy
                  </a>
                </li>
                <li>
                  <a
                    href="#2"
                    className="hover:text-black"
                    onClick={(e) => scrollToSection(e, "2")}
                  >
                    Conditions Under Which Refunds are Available
                  </a>
                </li>
                <li>
                  <a
                    href="#3"
                    className="hover:text-black"
                    onClick={(e) => scrollToSection(e, "3")}
                  >
                    Steps for Users to Request a Refund
                  </a>
                </li>
                <li>
                  <a
                    href="#4"
                    className="hover:text-black"
                    onClick={(e) => scrollToSection(e, "4")}
                  >
                    Time Frame for Processing Refunds
                  </a>
                </li>
                <li>
                  <a
                    href="#5"
                    className="hover:text-black"
                    onClick={(e) => scrollToSection(e, "5")}
                  >
                    Method of Refund
                  </a>
                </li>
                <li>
                  <a
                    href="#6"
                    className="hover:text-black"
                    onClick={(e) => scrollToSection(e, "6")}
                  >
                    Situations Where Refunds May Not Be Granted
                  </a>
                </li>
                <li>
                  <a
                    href="#7"
                    className="hover:text-black"
                    onClick={(e) => scrollToSection(e, "7")}
                  >
                    Notification of Changes to the Policy
                  </a>
                </li>
                <li>
                  <a
                    href="#8"
                    className="hover:text-black"
                    onClick={(e) => scrollToSection(e, "8")}
                  >
                    How Users Can Contact for Refund-Related Inquiries
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full md:w-3/4 lg:w-4/5 xl:w-5/6 px-4">
            <div
              className="wow fadeInUp mx-auto max-w-[800px]"
              data-wow-delay=".2s"
            >
              <h2
                id="1"
                className="text-lg md:text-xl lg:text-2xl font-semibold mb-3"
              >
                Overview of the Refund Policy
              </h2>
              <p className="mb-4 text-neutral-600">
                This Refund Policy outlines the terms under which customers of
                XplorEase can request a refund. Our policy is to ensure a fair
                and transparent process for handling refunds, respecting both
                the needs of our customers and the operational requirements of
                our service.
              </p>

              <h2
                id="2"
                className="text-lg md:text-xl lg:text-2xl font-semibold mb-3"
              >
                Conditions Under Which Refunds are Available
              </h2>
              <div className="mb-4 text-neutral-600">
                <p>Refunds may be requested under the following conditions:</p>
                <ul className="mt-2 ml-8 list-disc">
                  <li>
                    <span className="font-semibold">Non-usage:</span> If you
                    have not utilized our service after purchase.
                  </li>
                  <li>
                    <span className="font-semibold">
                      Service Unavailability:
                    </span>{" "}
                    In cases where XplorEase was not available due to technical
                    issues on our end.
                  </li>
                  <li>
                    <span className="font-semibold">Other Valid Reasons:</span>{" "}
                    Evaluated on a case-by-case basis, such as not receiving the
                    promised level of service.
                  </li>
                </ul>
              </div>

              <h2
                id="3"
                className="text-lg md:text-xl lg:text-2xl font-semibold mb-3"
              >
                Steps for Users to Request a Refund
              </h2>
              <div className="mb-4 text-neutral-600">
                <p>We use the collected data for various purposes:</p>
                <ol className="mt-2 ml-8 list-decimal">
                  <li>
                    Contact us via info@demetrix.in within 3 days of your
                    purchase.
                  </li>
                  <li>
                    Provide your account information and proof of purchase.
                  </li>
                  <li>Specify the reason for the refund request.</li>
                  <li>
                    Allow us 2-3 weeks to process your request and respond.
                  </li>
                </ol>
              </div>

              <h2
                id="4"
                className="text-lg md:text-xl lg:text-2xl font-semibold mb-3"
              >
                Time Frame for Processing Refunds
              </h2>
              <p className="mb-4 text-neutral-600">
                Refunds will generally be processed within 2-3 weeks after the
                refund request is approved.
              </p>

              <h2
                id="5"
                className="text-lg md:text-xl lg:text-2xl font-semibold mb-3"
              >
                Method of Refund
              </h2>
              <p className="mb-4 text-neutral-600">
                Refunds will be processed using the original method of payment,
                unless otherwise agreed upon. For example, if you made your
                payment by credit card, the refund will be credited to the same
                credit card.
              </p>

              <h2
                id="6"
                className="text-lg md:text-xl lg:text-2xl font-semibold mb-3"
              >
                Situations Where Refunds May Not Be Granted
              </h2>
              <div className="mb-4 text-neutral-600">
                <p>Refunds will not be granted in the following situations:</p>
                <ul className="mt-2 ml-8 list-disc">
                  <li>
                    <span className="font-semibold">Delayed requests:</span>{" "}
                    Refund requests made beyond the specified time period after
                    purchase.
                  </li>
                  <li>
                    <span className="font-semibold">Violation of TOS:</span> If
                    usage of the service was in violation of our Terms of
                    Service.
                  </li>
                  <li>
                    <span className="font-semibold">Partial Use:</span> If a
                    significant portion of the service has already been used.
                  </li>
                </ul>
              </div>

              <h2
                id="7"
                className="text-lg md:text-xl lg:text-2xl font-semibold mb-3"
              >
                Notification of Changes to the Policy
              </h2>
              <p className="mb-4 text-neutral-600">
                We reserve the right to modify or update this Refund Policy at
                any time. We will notify users of any changes by posting the
                updated policy on our website. Users are encouraged to review
                the Refund Policy periodically to stay informed of our refund
                procedures.
              </p>

              <h2
                id="8"
                className="text-lg md:text-xl lg:text-2xl font-semibold mb-3"
              >
                How Users Can Contact for Refund-Related Inquiries
              </h2>
              <p className="mb-4 text-neutral-600">
                For any questions regarding refunds or to request a refund,
                please{" "}
                <Link
                  to="/support"
                  className="text-primary underline hover:text-primary-dark"
                >
                  contact us
                </Link>{" "}
                at{" "}
                <a
                  href="mailto:info@demetrix.in"
                  className="text-primary underline hover:text-primary-dark"
                >
                  info@demetrix.in
                </a>
                We are committed to addressing your concerns as quickly and efficiently as possible.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute top-5 right-0 z-[-1]">
        <svg
          width="238"
          height="531"
          viewBox="0 0 238 531"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            opacity="0.3"
            x="422.819"
            y="-70.8145"
            width="196"
            height="541.607"
            rx="2"
            transform="rotate(51.2997 422.819 -70.8145)"
            fill="url(#paint0_linear_83:2)"
          />
          <rect
            opacity="0.3"
            x="426.568"
            y="144.886"
            width="59.7544"
            height="541.607"
            rx="2"
            transform="rotate(51.2997 426.568 144.886)"
            fill="url(#paint1_linear_83:2)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_83:2"
              x1="517.152"
              y1="-251.373"
              x2="517.152"
              y2="459.865"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4A6CF7" />
              <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_83:2"
              x1="455.327"
              y1="-35.673"
              x2="455.327"
              y2="675.565"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4A6CF7" />
              <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="absolute left-0 bottom-5 z-[-1]">
        <svg
          width="279"
          height="106"
          viewBox="0 0 279 106"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g opacity="0.5">
            <path
              d="M-57 12L50.0728 74.8548C55.5501 79.0219 70.8513 85.7589 88.2373 79.3692C109.97 71.3821 116.861 60.9642 156.615 63.7423C178.778 65.291 195.31 69.2985 205.911 62.3533C216.513 55.408 224.994 47.7682 243.016 49.1572C255.835 50.1453 265.278 50.8936 278 45.3373"
              stroke="url(#paint0_linear_72:302)"
            />
            <path
              d="M-57 1L50.0728 63.8548C55.5501 68.0219 70.8513 74.7589 88.2373 68.3692C109.97 60.3821 116.861 49.9642 156.615 52.7423C178.778 54.291 195.31 58.2985 205.911 51.3533C216.513 44.408 224.994 36.7682 243.016 38.1572C255.835 39.1453 265.278 39.8936 278 34.3373"
              stroke="url(#paint1_linear_72:302)"
            />
            <path
              d="M-57 23L50.0728 85.8548C55.5501 90.0219 70.8513 96.7589 88.2373 90.3692C109.97 82.3821 116.861 71.9642 156.615 74.7423C178.778 76.291 195.31 80.2985 205.911 73.3533C216.513 66.408 224.994 58.7682 243.016 60.1572C255.835 61.1453 265.278 61.8936 278 56.3373"
              stroke="url(#paint2_linear_72:302)"
            />
            <path
              d="M-57 35L50.0728 97.8548C55.5501 102.022 70.8513 108.759 88.2373 102.369C109.97 94.3821 116.861 83.9642 156.615 86.7423C178.778 88.291 195.31 92.2985 205.911 85.3533C216.513 78.408 224.994 70.7682 243.016 72.1572C255.835 73.1453 265.278 73.8936 278 68.3373"
              stroke="url(#paint3_linear_72:302)"
            />
          </g>
          <defs>
            <linearGradient
              id="paint0_linear_72:302"
              x1="256.267"
              y1="53.6717"
              x2="-40.8688"
              y2="8.15715"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4A6CF7" stopOpacity="0" />
              <stop offset="1" stopColor="#4A6CF7" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_72:302"
              x1="256.267"
              y1="42.6717"
              x2="-40.8688"
              y2="-2.84285"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4A6CF7" stopOpacity="0" />
              <stop offset="1" stopColor="#4A6CF7" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_72:302"
              x1="256.267"
              y1="64.6717"
              x2="-40.8688"
              y2="19.1572"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4A6CF7" stopOpacity="0" />
              <stop offset="1" stopColor="#4A6CF7" />
            </linearGradient>
            <linearGradient
              id="paint3_linear_72:302"
              x1="256.267"
              y1="76.6717"
              x2="-40.8688"
              y2="31.1572"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4A6CF7" stopOpacity="0" />
              <stop offset="1" stopColor="#4A6CF7" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </section>
  );
}

export default RefundPolicyHero;
